import * as React from 'react';

import { toNumber } from 'lodash';
import { compose, DataValue, graphql } from 'react-apollo';

import BoxBody from 'components/BoxBody';
import Button from 'components/Button';
import CheckboxList from 'components/CheckboxList';
import DialogContent from 'components/DialogContent';
import LoadingIndicator from 'components/LoadingIndicator';
// @ts-ignore
import Redirect from 'components/Redirect';
import Text from 'components/Text';
import EventTracking from 'lib/EventTracking';
// @ts-ignore
import trackView from 'lib/trackView';

import SetupRosterPoll from './SetupRosterPoll';

import { Backbone$Id } from 'types/typescript/Backbone';
import {
  getProposedGroups,
  getProposedGroups_me_proposed_groups,
} from '../queries/__generated__/getProposedGroups';
import { RosterChoose, RosterChooseVariables } from '../queries/__generated__/RosterChoose';

import GET_PROPOSED_GROUPS from '../queries/GetProposedGroupsQuery.gql';
import ROSTER_CHOOSE_MUTATION from '../queries/RosterChooseMutation.gql';

import './styles/SetupRosterChoose.scss';

export type ProposedGroup = {
  id: Backbone$Id;
  class_name: string;
  parent_count: number;
  student_count: number;
};

type Props = {
  claimClasses: (classIds: Array<number>) => Promise<string>;
  proposedGroups?: getProposedGroups_me_proposed_groups[] | null;
};

type State = {
  selectedGroups?: Array<number> | null;
  statusEndpoint?: string;
};

export class SetupRosterChoose extends React.Component<Props, State> {
  state: State = {
    selectedGroups: null,
    statusEndpoint: '',
  };

  handleCheckboxChange = (selectedGroups: Array<unknown>) => {
    this.setState({ selectedGroups: selectedGroups.map(toNumber) });
  };

  handleCheckboxSubmit = () => {
    const { proposedGroups } = this.props;
    const { selectedGroups } = this.state;
    const groupsToSend =
      selectedGroups || (proposedGroups && proposedGroups.map(g => toNumber(g.id))) || [];

    EventTracking.tap('sis_roster_choose', 'submit', null, {
      total_group_count: proposedGroups && proposedGroups.length,
      selected_group_count: groupsToSend.length,
    });
    this.props
      .claimClasses(groupsToSend)
      .then(statusEndpoint => {
        this.setState({ statusEndpoint });
      })
      .catch(err => {
        console.warn(err, err && err.stack);
        console.error('failed to handle submit in SetupRosterChoose');
      });
  };

  renderProposedGroupLabel(proposedGroup: ProposedGroup) {
    const memberCount = proposedGroup.parent_count + proposedGroup.student_count;
    return (
      <div className="SetupRosterChoose-item">
        <Text element="div" weight="bold">
          {proposedGroup.class_name}
        </Text>
        <Text element="div" color="thunder" weight="light" size="xxxsmall">
          {memberCount} students and parents
        </Text>
      </div>
    );
  }

  renderChooseScreen(proposedGroups: Array<ProposedGroup>) {
    const proposedGroupsOptions = new Map(
      // we can only pass in strings to the check box.  we need to convert back to numbers before sending to GQL
      proposedGroups.map(g => [String(g.id), this.renderProposedGroupLabel(g)])
    );

    return (
      <DialogContent
        key="choose"
        size="small"
        className="SetupRosterChoose-header"
        title="Which classes would you like to import to Remind?"
        subtext="Importing will automatically create a new class and add participants. This won't replace any of the classes you currently own."
        image={require('assets/images/roster-choose.png')}
        imageSize="xsmall">
        <BoxBody>
          <CheckboxList
            defaultChecked={proposedGroups.map(g => g.id)}
            options={proposedGroupsOptions}
            onChange={this.handleCheckboxChange}
            showRowDividers
          />
          <Button onClick={this.handleCheckboxSubmit} className="SetupRosterChoose-submit">
            Continue
          </Button>
        </BoxBody>
      </DialogContent>
    );
  }

  render() {
    const { proposedGroups } = this.props;
    const { statusEndpoint } = this.state;

    if (statusEndpoint) {
      return <SetupRosterPoll statusEndpoint={statusEndpoint} proposedGroups={proposedGroups} />;
    } else if (proposedGroups) {
      if (proposedGroups.length === 0) {
        return <Redirect to="/landing" />;
      } else {
        return this.renderChooseScreen(proposedGroups);
      }
    } else {
      // The key is so that when we render the actual content it will redraw the DialogContent
      // Because the two elements are different height, if we don't do this, the choose dialog
      // will get rendered really low on the page.
      return (
        <DialogContent key="loading">
          <BoxBody>
            <LoadingIndicator />
          </BoxBody>
        </DialogContent>
      );
    }
  }
}

const withMutation = graphql(ROSTER_CHOOSE_MUTATION, {
  props({ mutate }) {
    const typedMutate = (mutate as any) as (
      arg1: { variables: RosterChooseVariables }
    ) => Promise<{ data: RosterChoose }>;
    return {
      claimClasses(classIds: Array<number>) {
        return (
          typedMutate &&
          typedMutate({ variables: { classIds } }).then(
            ({ data: { claimProposedClasses } }) => claimProposedClasses.status_endpoint
          )
        );
      },
    };
  },
});

const withGroups = graphql(GET_PROPOSED_GROUPS, {
  props: ({ data }) => {
    const { me } = (data as any) as DataValue<getProposedGroups>;
    return me ? { proposedGroups: me.proposed_groups } : null;
  },
});

export default compose(
  withMutation,
  withGroups,
  (comp: React.ReactElement) => trackView(comp, 'sis_roster_choose')
)(SetupRosterChoose);
